/**
 * Vboard — vision boards built from media library images, Google Drive
 * images, or images attached to goals. Supports carousel (timed slides)
 * and collage layouts, plus a fullscreen display mode (Esc to exit).
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Trash2, Pencil, Images, ChevronLeft, ChevronRight, Maximize2,
  Target, HardDrive, FolderOpen, X, Loader2, ImageOff,
} from "lucide-react";
import { createVisionBoardBodyImagesMax } from "@workspace/api-zod";
import { isFutureGoal } from "@/lib/goal-visibility";

type VisionBoardImage = {
  id: string;
  source: "media" | "goal" | "drive";
  mediaFileId?: string;
  goalId?: string;
  driveFileId?: string;
  url?: string;
  name?: string;
};

type VisionBoard = {
  id: string;
  name: string;
  displayMode: "carousel" | "collage";
  slideIntervalSeconds: number;
  images: VisionBoardImage[];
};

// Single source of truth: generated from maxItems in lib/api-spec/openapi.yaml
// (VisionBoardInput.images), so the picker cap can't drift from the server cap.
const MAX_IMAGES = createVisionBoardBodyImagesMax;

// Media fileType values are extensions ("jpeg", "jpg", "png"), not MIME types.
const IMAGE_FILE_TYPES = new Set(["jpeg", "jpg", "png", "gif", "webp"]);
function isImageFileType(fileType?: string | null): boolean {
  if (!fileType) return false;
  const t = fileType.toLowerCase();
  return IMAGE_FILE_TYPES.has(t) || t.startsWith("image");
}

/* ---------- image rendering (media/goal images need the content endpoint) ---------- */

const contentUrlCache = new Map<string, string>();

function useImageSrc(img: VisionBoardImage): { src: string | null; failed: boolean } {
  const [src, setSrc] = useState<string | null>(() =>
    img.source === "drive" ? img.url || null : (img.mediaFileId && contentUrlCache.get(img.mediaFileId)) || null,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (img.source === "drive") {
      setSrc(img.url || null);
      setFailed(!img.url);
      return;
    }
    if (!img.mediaFileId) { setFailed(true); return; }
    const cached = contentUrlCache.get(img.mediaFileId);
    if (cached) { setSrc(cached); return; }
    fetch(`/api/media/${img.mediaFileId}/content`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error("load failed"))))
      .then(data => {
        if (cancelled) return;
        if (data?.fileUrl) {
          contentUrlCache.set(img.mediaFileId!, data.fileUrl);
          setSrc(data.fileUrl);
        } else setFailed(true);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [img.source, img.mediaFileId, img.url]);
  return { src, failed };
}

function BoardImage({ img, className, cover = true }: { img: VisionBoardImage; className?: string; cover?: boolean }) {
  const { src, failed } = useImageSrc(img);
  if (failed) {
    return (
      <div className={`flex items-center justify-center bg-muted text-muted-foreground ${className || ""}`}>
        <ImageOff className="w-6 h-6" />
      </div>
    );
  }
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-muted ${className || ""}`}>
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <img src={src} alt={img.name || "Vision board image"} className={`${cover ? "object-cover" : "object-contain"} ${className || ""}`} draggable={false} />;
}

/* ---------- carousel viewer ---------- */

function CarouselView({ board, fill }: { board: VisionBoard; fill?: boolean }) {
  const [index, setIndex] = useState(0);
  const count = board.images.length;
  useEffect(() => { setIndex(0); }, [board.id]);
  useEffect(() => {
    if (count < 2) return;
    const iv = setInterval(
      () => setIndex(i => (i + 1) % count),
      Math.max(1, board.slideIntervalSeconds) * 1000,
    );
    return () => clearInterval(iv);
  }, [count, board.slideIntervalSeconds, board.id]);
  if (count === 0) return null;
  const img = board.images[Math.min(index, count - 1)];
  return (
    <div className={`relative w-full ${fill ? "h-full" : "aspect-video"} bg-black/90 rounded-lg overflow-hidden group`}>
      <BoardImage key={img.id} img={img} cover={false} className="w-full h-full" />
      {count > 1 && (
        <>
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => setIndex(i => (i - 1 + count) % count)}
            data-testid="button-carousel-prev"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => setIndex(i => (i + 1) % count)}
            data-testid="button-carousel-next"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {board.images.map((im, i) => (
              <button
                key={im.id}
                className={`w-2 h-2 rounded-full ${i === index ? "bg-white" : "bg-white/40"}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- collage viewer (grid sizes with image count) ---------- */

function collageCols(n: number): string {
  if (n <= 1) return "grid-cols-1";
  if (n === 2) return "grid-cols-2";
  if (n <= 4) return "grid-cols-2";
  if (n <= 6) return "grid-cols-3";
  return "grid-cols-4";
}

function CollageView({ board, fill }: { board: VisionBoard; fill?: boolean }) {
  const n = board.images.length;
  if (n === 0) return null;
  return (
    <div className={`grid ${collageCols(n)} gap-2 w-full ${fill ? "h-full" : ""} bg-black/90 rounded-lg p-2 overflow-hidden`}>
      {board.images.map(img => (
        <BoardImage key={img.id} img={img} className="w-full h-full min-h-24 rounded-md" />
      ))}
    </div>
  );
}

/* ---------- fullscreen wrapper ---------- */

function FullscreenViewer({ board, onClose }: { board: VisionBoard; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest onClose in a ref so the effect below runs exactly ONCE on
  // mount. With `onClose` in the dep array, any parent re-render (e.g. a
  // background query refetch) recreated the callback, re-ran the effect,
  // exited fullscreen in cleanup, and the resulting fullscreenchange event
  // immediately closed the viewer.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const el = ref.current;
    let entered = false;
    el?.requestFullscreen?.().then(() => { entered = true; }).catch(() => {});
    const onChange = () => {
      if (entered && !document.fullscreenElement) onCloseRef.current();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("keydown", onKey);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div ref={ref} className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="fullscreen-viewer">
      <div className="w-full h-full p-2">
        {board.displayMode === "carousel"
          ? <CarouselView board={board} fill />
          : <CollageView board={board} fill />}
      </div>
      <button
        className="absolute top-3 right-3 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
        onClick={onClose}
        data-testid="button-exit-fullscreen"
        aria-label="Exit fullscreen"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="absolute bottom-3 right-4 text-white/50 text-xs">Press Esc to exit</div>
    </div>
  );
}

/* ---------- image picker (media library / goals / google drive) ---------- */

function ImagePicker({
  open, onClose, selected, onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  selected: VisionBoardImage[];
  onConfirm: (images: VisionBoardImage[]) => void;
}) {
  const [picked, setPicked] = useState<VisionBoardImage[]>(selected);
  const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);
  useEffect(() => { if (open) setPicked(selected); }, [open, selected]);

  const { data: mediaFiles = [] } = useQuery<any[]>({
    queryKey: ["/api/media"],
    enabled: open,
  });
  const imageMedia = useMemo(
    () => mediaFiles.filter(f => isImageFileType(f.fileType)),
    [mediaFiles],
  );

  const { data: goals = [] } = useQuery<any[]>({
    queryKey: ["/api/goals"],
    enabled: open,
  });
  const activeGoals = useMemo(() => goals.filter(g => g.status === "active" && !isFutureGoal(g)), [goals]);

  const { data: goalMedia = [], isLoading: goalMediaLoading } = useQuery<any[]>({
    queryKey: ["/api/goals", expandedGoalId, "media"],
    queryFn: async () => {
      const r = await fetch(`/api/goals/${expandedGoalId}/media`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load goal images");
      return r.json();
    },
    enabled: open && !!expandedGoalId,
  });
  const goalImages = useMemo(
    () => goalMedia.filter((gm: any) => isImageFileType(gm.media?.fileType)),
    [goalMedia],
  );

  const { data: driveFiles = [], isError: driveError } = useQuery<any[]>({
    queryKey: ["/api/google-drive/files"],
    enabled: open,
    retry: false,
  });
  const driveImages = useMemo(
    () => (Array.isArray(driveFiles) ? driveFiles : []).filter(
      (f: any) => (f.mimeType || "").startsWith("image") && f.thumbnailLink,
    ),
    [driveFiles],
  );

  const isPicked = (key: string) => picked.some(p => p.id === key);
  const toggle = (img: VisionBoardImage) => {
    setPicked(prev => {
      if (prev.some(p => p.id === img.id)) return prev.filter(p => p.id !== img.id);
      if (prev.length >= MAX_IMAGES) return prev;
      return [...prev, img];
    });
  };

  const tile = (key: string, img: VisionBoardImage, thumb: React.ReactNode, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => toggle(img)}
      className={`relative rounded-md overflow-hidden border-2 text-left ${isPicked(key) ? "border-primary ring-2 ring-primary/40" : "border-transparent"}`}
      data-testid={`picker-image-${key}`}
    >
      {thumb}
      <div className="px-1.5 py-1 text-xs truncate bg-background/90">{label}</div>
      {isPicked(key) && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
          {picked.findIndex(p => p.id === key) + 1}
        </div>
      )}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Choose images ({picked.length}/{MAX_IMAGES})</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="media" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="media" data-testid="tab-picker-media"><FolderOpen className="w-4 h-4 mr-1.5" />My Media</TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-picker-goals"><Target className="w-4 h-4 mr-1.5" />From Goals</TabsTrigger>
            <TabsTrigger value="drive" data-testid="tab-picker-drive"><HardDrive className="w-4 h-4 mr-1.5" />Google Drive</TabsTrigger>
          </TabsList>

          <TabsContent value="media" className="flex-1 overflow-y-auto mt-3">
            {imageMedia.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No images in your media library yet. Upload some on the Media page first.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {imageMedia.map(f =>
                  tile(`media-${f.id}`,
                    { id: `media-${f.id}`, source: "media", mediaFileId: f.id, name: f.name },
                    <BoardImage img={{ id: `t-${f.id}`, source: "media", mediaFileId: f.id }} className="w-full h-24" />,
                    f.name || "Image"),
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="goals" className="flex-1 overflow-y-auto mt-3 space-y-2">
            {activeGoals.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No active goals found.</p>
            ) : activeGoals.map(g => (
              <div key={g.id} className="border rounded-md">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium"
                  onClick={() => setExpandedGoalId(expandedGoalId === g.id ? null : g.id)}
                  data-testid={`picker-goal-${g.id}`}
                >
                  <span className="flex items-center gap-2"><Target className="w-4 h-4 text-primary" />{g.title}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${expandedGoalId === g.id ? "rotate-90" : ""}`} />
                </button>
                {expandedGoalId === g.id && (
                  <div className="px-3 pb-3">
                    {goalMediaLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto my-4" />
                    ) : goalImages.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No images attached to this goal.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {goalImages.map((gm: any) =>
                          tile(`goal-${gm.media.id}`,
                            { id: `goal-${gm.media.id}`, source: "goal", mediaFileId: gm.media.id, goalId: g.id, name: gm.media.name },
                            <BoardImage img={{ id: `t-${gm.media.id}`, source: "goal", mediaFileId: gm.media.id }} className="w-full h-24" />,
                            gm.media.name || "Image"),
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="drive" className="flex-1 overflow-y-auto mt-3">
            {driveError ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Couldn't reach Google Drive. Connect your Google account on the Drive page, then try again.</p>
            ) : driveImages.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No images found in your Google Drive.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {driveImages.map((f: any) =>
                  tile(`drive-${f.driveFileId || f.id}`,
                    { id: `drive-${f.driveFileId || f.id}`, source: "drive", driveFileId: f.driveFileId || f.id, url: f.thumbnailLink, name: f.name },
                    <img src={f.thumbnailLink} alt={f.name} className="w-full h-24 object-cover" />,
                    f.name || "Image"),
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { onConfirm(picked); onClose(); }} data-testid="button-confirm-images">
            Use {picked.length} image{picked.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- board editor dialog ---------- */

function BoardEditor({
  open, onClose, board,
}: {
  open: boolean;
  onClose: () => void;
  board: VisionBoard | null; // null = create
}) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [displayMode, setDisplayMode] = useState<"carousel" | "collage">("carousel");
  const [interval, setIntervalSecs] = useState(5);
  const [images, setImages] = useState<VisionBoardImage[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const moveImage = (from: number, to: number) => {
    setImages(prev => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  useEffect(() => {
    if (open) {
      setName(board?.name || "");
      setDisplayMode(board?.displayMode || "carousel");
      setIntervalSecs(board?.slideIntervalSeconds || 5);
      setImages(board?.images || []);
    }
  }, [open, board]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { name: name.trim(), displayMode, slideIntervalSeconds: interval, images };
      if (board) return apiRequest("PATCH", `/api/vision-boards/${board.id}`, payload);
      return apiRequest("POST", "/api/vision-boards", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vision-boards"] });
      toast({ title: board ? "Vision board updated" : "Vision board created" });
      onClose();
    },
    onError: () => {
      toast({ title: "Couldn't save the vision board", description: "Please check the fields and try again.", variant: "destructive" });
    },
  });

  return (
    <>
      {/* While the picker is open, ignore ALL close requests for the editor:
          picker interactions (confirm, cancel, Escape, overlay click) must
          never dismiss the editor and discard the unsaved form. */}
      <Dialog open={open} onOpenChange={o => { if (!o && !pickerOpen) onClose(); }}>
        {/* The image picker is a sibling dialog, so interactions inside it
            (including the closing "Use N images" click) register as outside
            interactions here and would silently discard the whole form.
            Editors shouldn't dismiss on outside clicks anyway — close via
            Cancel, the X button, or Escape. */}
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-y-auto"
          onInteractOutside={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{board ? "Edit Vision Board" : "New Vision Board"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="vb-name">Name</Label>
              <Input id="vb-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Dream Home 2027" data-testid="input-board-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Layout</Label>
                <Select value={displayMode} onValueChange={v => setDisplayMode(v as any)}>
                  <SelectTrigger data-testid="select-display-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carousel">Carousel (slides)</SelectItem>
                    <SelectItem value="collage">Collage (all at once)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {displayMode === "carousel" && (
                <div className="space-y-1.5">
                  <Label htmlFor="vb-interval">Seconds per image</Label>
                  <Input
                    id="vb-interval"
                    type="number"
                    min={1}
                    max={3600}
                    value={interval}
                    onChange={e => setIntervalSecs(Math.max(1, Math.min(3600, parseInt(e.target.value) || 5)))}
                    data-testid="input-slide-interval"
                  />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Images ({images.length}/{MAX_IMAGES})</Label>
                <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="button-open-picker">
                  <Images className="w-4 h-4 mr-1.5" />Choose Images
                </Button>
              </div>
              {images.length === 0 ? (
                <p className="text-sm text-muted-foreground border rounded-md p-4 text-center">No images yet — choose from your media, Google Drive, or your goals.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, i) => (
                    <div
                      key={img.id}
                      className={`relative rounded-md overflow-hidden group cursor-grab ${dragIndex === i ? "opacity-50" : ""}`}
                      draggable
                      onDragStart={e => {
                        // Firefox requires setData for an HTML5 drag to start.
                        e.dataTransfer.setData("text/plain", img.id);
                        e.dataTransfer.effectAllowed = "move";
                        setDragIndex(i);
                      }}
                      onDragEnd={() => setDragIndex(null)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault();
                        if (dragIndex !== null) moveImage(dragIndex, i);
                        setDragIndex(null);
                      }}
                      data-testid={`board-image-${i}`}
                    >
                      <BoardImage img={img} className="w-full h-20" />
                      <button
                        type="button"
                        className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100"
                        onClick={() => setImages(prev => prev.filter(p => p.id !== img.id))}
                        aria-label="Remove image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          className="p-0.5 rounded-full bg-black/60 text-white disabled:opacity-30"
                          onClick={() => moveImage(i, i - 1)}
                          disabled={i === 0}
                          aria-label="Move image earlier"
                          data-testid={`button-move-image-back-${i}`}
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          className="p-0.5 rounded-full bg-black/60 text-white disabled:opacity-30"
                          onClick={() => moveImage(i, i + 1)}
                          disabled={i === images.length - 1}
                          aria-label="Move image later"
                          data-testid={`button-move-image-forward-${i}`}
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!name.trim() || saveMutation.isPending}
              data-testid="button-save-board"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {board ? "Save Changes" : "Create Board"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selected={images}
        onConfirm={setImages}
      />
    </>
  );
}

/* ---------- page ---------- */

export default function VboardPage() {
  const { toast } = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<VisionBoard | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [fullscreenBoard, setFullscreenBoard] = useState<VisionBoard | null>(null);

  const { data: boards = [], isLoading } = useQuery<VisionBoard[]>({
    queryKey: ["/api/vision-boards"],
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/vision-boards/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vision-boards"] });
      toast({ title: "Vision board deleted" });
    },
    onError: () => {
      toast({ title: "Couldn't delete the vision board", variant: "destructive" });
    },
  });

  const viewingBoard = boards.find(b => b.id === viewingId) || null;

  const openCreate = useCallback(() => { setEditingBoard(null); setEditorOpen(true); }, []);
  const openEdit = useCallback((b: VisionBoard) => { setEditingBoard(b); setEditorOpen(true); }, []);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Vboard</h1>
          <p className="text-sm text-muted-foreground">Vision boards — keep the pictures of what you're working toward in front of you.</p>
        </div>
        <Button onClick={openCreate} data-testid="button-new-board">
          <Plus className="w-4 h-4 mr-1.5" />New Vision Board
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : boards.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <Images className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="font-medium">No vision boards yet</p>
            <p className="text-sm text-muted-foreground">Create one and fill it with images from your media library, Google Drive, or your goals.</p>
            <Button onClick={openCreate} data-testid="button-empty-new-board"><Plus className="w-4 h-4 mr-1.5" />Create your first board</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map(b => (
            <Card key={b.id} className={`overflow-hidden ${viewingId === b.id ? "ring-2 ring-primary" : ""}`}>
              <button
                type="button"
                className="w-full aspect-video bg-muted relative"
                onClick={() => setViewingId(viewingId === b.id ? null : b.id)}
                data-testid={`board-card-${b.id}`}
              >
                {b.images[0] ? (
                  <BoardImage img={b.images[0]} className="w-full h-full" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Images className="w-8 h-8" /></div>
                )}
                <Badge className="absolute top-2 left-2" variant="secondary">
                  {b.displayMode === "carousel" ? "Carousel" : "Collage"} · {b.images.length} image{b.images.length === 1 ? "" : "s"}
                </Badge>
              </button>
              <CardContent className="p-3 flex items-center justify-between gap-2">
                <span className="font-medium truncate">{b.name}</span>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setFullscreenBoard(b)} disabled={b.images.length === 0} data-testid={`button-fullscreen-${b.id}`} aria-label="Fullscreen">
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`button-edit-${b.id}`} aria-label="Edit">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => { if (window.confirm(`Delete "${b.name}"?`)) deleteMutation.mutate(b.id); }} data-testid={`button-delete-${b.id}`} aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {viewingBoard && viewingBoard.images.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{viewingBoard.name}</h2>
            <Button size="sm" variant="outline" onClick={() => setFullscreenBoard(viewingBoard)} data-testid="button-view-fullscreen">
              <Maximize2 className="w-4 h-4 mr-1.5" />Fullscreen
            </Button>
          </div>
          {viewingBoard.displayMode === "carousel"
            ? <CarouselView board={viewingBoard} />
            : <CollageView board={viewingBoard} />}
        </div>
      )}

      <BoardEditor open={editorOpen} onClose={() => setEditorOpen(false)} board={editingBoard} />
      {fullscreenBoard && (
        <FullscreenViewer board={fullscreenBoard} onClose={() => setFullscreenBoard(null)} />
      )}
    </div>
  );
}
